import { IconX } from "@tabler/icons-react";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import clsx from "clsx";
import { useState } from "react";

function AlertErrors<T>({ errors }: { errors: Record<string, T> }) {
	return (
		<ul className="text-base">
			{Object.entries(errors).map(([key, value]) => (
				<li key={key}>{value as string}</li>
			))}
		</ul>
	);
}

type AlertProps = {
	icon?: JSX.Element;
	title?: JSX.Element | string;
	children?: React.ReactNode;
	className?: string;
	allowClose?: boolean;
} & VariantProps<typeof alertVariants>;

const alertVariants = cva("relative flex flex-row w-full p-4 gap-2 rounded-md", {
	variants: {
		type: {
			default: "bg-slate-100/70 text-foreground",
			warning: "bg-orange-50 text-yellow-700",
			error: "bg-red-50 text-red-700",
			success: "bg-green-50 text-green-700",
			info: "bg-sky-50 text-sky-700",
		},
		titleColor: {
			default: "text-foreground",
			warning: "text-yellow-700",
			error: "text-red-700",
			success: "text-green-700",
			info: "text-sky-700",
		},
		iconColor: {
			default: "text-foreground",
			warning: "text-yellow-700",
			error: "text-red-700",
			success: "text-green-700",
			info: "text-sky-700",
		},
	},
	defaultVariants: {
		type: "default",
	},
});

function Alert({ icon, type = "default", children, title, className, allowClose = false }: AlertProps) {
	const [visible, setVisible] = useState(true);

	if (!visible) return null;

	return (
		<div className={clsx(alertVariants({ type }), className)}>
			{allowClose && (
				<div className="absolute top-2 right-2">
					<button type="button" className="opacity-50 hover:opacity-100" onClick={() => setVisible(false)}>
						<IconX size={18} stroke={1.5} />
					</button>
				</div>
			)}

			{icon && <div className={clsx("inline-flex self-start w-auto", alertVariants({ iconColor: type }).iconColor)}>{icon}</div>}

			<div className="flex flex-col grow shrink w-full gap-1">
				{title && <span className={clsx("block w-full text-base font-semibold", alertVariants({ type, titleColor: type }).titleColor)}>{title}</span>}
				<div className="flex flex-col w-full text-base gap-2">{children}</div>
			</div>
		</div>
	);
}

Alert.Errors = AlertErrors;

export default Alert;
